import { useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { SortOrders } from '@confluence/template-utils';
import { isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';

import {
	type BLANK_PAGE_QUICK_ACTIONS_MULTI_VARIANT_COHORTS,
	BPQA_EXCLUDED_TEMPLATE_KEYS,
	TEMPLATE_LIMIT,
} from './bpqaConst';
import { SuggestedTemplatesQuery } from './queries/SuggestedTemplatesQuery.graphql';
import type {
	SuggestedTemplatesQuery as SuggestedTemplatesQueryType,
	SuggestedTemplatesQueryVariables,
	SuggestedTemplatesQuery_allTemplates_nodes as Template,
} from './queries/__types__/SuggestedTemplatesQuery.ts';

type SuggestedTemplatesProps = {
	cohort: BLANK_PAGE_QUICK_ACTIONS_MULTI_VARIANT_COHORTS;
	spaceKey: string;
};

export const useSuggestedTemplates = ({ cohort, spaceKey }: SuggestedTemplatesProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();
	const { data, error, loading } = useQuery<
		SuggestedTemplatesQueryType,
		SuggestedTemplatesQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SuggestedTemplatesQuery,
		{
			variables: {
				spaceKey,
				sortingScheme: SortOrders.RELEVANCY_SORT,
				limit: TEMPLATE_LIMIT,
			},
			skip: !spaceKey,
		},
	);

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}

		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'BlankPageQuickActions',
				action: 'error',
				actionSubject: 'useSuggestedTemplates',
				attributes: {
					cohort,
					version: '2',
				},
			},
		}).fire();
	}

	const suggestedTemplates = (data?.allTemplates?.nodes as Template[]) || [];

	// we want to filter out the blog post template and legacy
	// editor template, so we get 5 templates and return the
	// first 3 excluding them if they exist
	const templates = suggestedTemplates
		?.filter(
			(template) =>
				template.itemModuleCompleteKey !== BPQA_EXCLUDED_TEMPLATE_KEYS.BLOG_POST &&
				template.blueprintModuleCompleteKey !== BPQA_EXCLUDED_TEMPLATE_KEYS.LEGACY_EDITOR &&
				(!isLivePage ||
					template.blueprintModuleCompleteKey !== BPQA_EXCLUDED_TEMPLATE_KEYS.LIVE_MEETING_NOTES),
		)
		?.slice(0, 3);

	return { error, templates, loading };
};
